/**
 * CRUD and migration for multi-version seating plans.
 */

import {
  addDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  runTransaction,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import type {
  Guest,
  SeatingCanvasInset,
  SeatingPlan,
  Table,
  Wedding,
} from "@amazing-wedding/shared-types";
import {
  DEFAULT_SEATING_CANVAS_INSET,
  SEATING_PLAN_NAME_MAX,
  guestSeatingFieldPath,
} from "@amazing-wedding/shared-types";
import { SeatingError } from "./seating-errors";
import {
  guestRef,
  legacyRoomElementsCol,
  legacyTablesCol,
  planRoomElementRef,
  planRoomElementsCol,
  planTableRef,
  planTablesCol,
  seatingPlanRef,
  seatingPlansCol,
  weddingRef,
} from "./firestore-paths";

const BATCH_CHUNK = 400;
const MAX_PLANS = 20;

function trimPlanName(name: string): string {
  return name.trim().slice(0, SEATING_PLAN_NAME_MAX);
}

export async function createSeatingPlan(
  weddingId: string,
  name: string,
  options?: { seatingCanvasInset?: SeatingCanvasInset; seatingRoomWidthM?: number },
): Promise<string> {
  const existing = await getDocs(seatingPlansCol(weddingId));
  if (existing.size >= MAX_PLANS) {
    throw new SeatingError("maxPlansReached", { max: MAX_PLANS });
  }

  const planName = trimPlanName(name) || `Plan ${existing.size + 1}`;
  const ref = await addDoc(seatingPlansCol(weddingId), {
    name: planName,
    seatingCanvasInset: options?.seatingCanvasInset ?? null,
    seatingRoomWidthM: options?.seatingRoomWidthM ?? null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function setActiveSeatingPlan(weddingId: string, planId: string): Promise<void> {
  await updateDoc(weddingRef(weddingId), {
    activeSeatingPlanId: planId,
    updatedAt: serverTimestamp(),
  });
}

export async function renameSeatingPlan(
  weddingId: string,
  planId: string,
  name: string,
): Promise<void> {
  const planName = trimPlanName(name);
  if (!planName) throw new SeatingError("planNameEmpty");
  await updateDoc(seatingPlanRef(weddingId, planId), {
    name: planName,
    updatedAt: serverTimestamp(),
  });
}

export type SeatingPlanPatch = Partial<
  Pick<SeatingPlan, "name" | "seatingCanvasInset" | "seatingRoomWidthM">
>;

export async function updateSeatingPlan(
  weddingId: string,
  planId: string,
  patch: SeatingPlanPatch,
): Promise<void> {
  await updateDoc(seatingPlanRef(weddingId, planId), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}

async function commitInChunks(
  ops: Array<(batch: ReturnType<typeof writeBatch>) => void>,
): Promise<void> {
  for (let i = 0; i < ops.length; i += BATCH_CHUNK) {
    const batch = writeBatch(db);
    ops.slice(i, i + BATCH_CHUNK).forEach((op) => op(batch));
    await batch.commit();
  }
}

export async function deleteSeatingPlan(
  weddingId: string,
  planId: string,
  allPlanIds: string[],
  activePlanId: string | null | undefined,
  guests: Guest[],
  legacyPlanId?: string | null,
): Promise<void> {
  if (allPlanIds.length <= 1) {
    throw new SeatingError("cannotDeleteLastPlan");
  }

  const tableSnap = await getDocs(planTablesCol(weddingId, planId));
  const roomSnap = await getDocs(planRoomElementsCol(weddingId, planId));

  const ops: Array<(batch: ReturnType<typeof writeBatch>) => void> = [];

  for (const d of tableSnap.docs) {
    ops.push((batch) => batch.delete(planTableRef(weddingId, planId, d.id)));
  }
  for (const d of roomSnap.docs) {
    ops.push((batch) => batch.delete(planRoomElementRef(weddingId, planId, d.id)));
  }
  for (const g of guests) {
    if (g.seatingByPlan?.[planId] !== undefined) {
      ops.push((batch) =>
        batch.update(guestRef(weddingId, g.id), {
          [guestSeatingFieldPath(planId)]: deleteField(),
          updatedAt: serverTimestamp(),
        }),
      );
    }
    if (legacyPlanId === planId && g.tableId) {
      ops.push((batch) =>
        batch.update(guestRef(weddingId, g.id), {
          tableId: null,
          seatNumber: null,
          updatedAt: serverTimestamp(),
        }),
      );
    }
  }
  ops.push((batch) => batch.delete(seatingPlanRef(weddingId, planId)));

  await commitInChunks(ops);

  if (activePlanId === planId) {
    const remaining = allPlanIds.filter((id) => id !== planId);
    const next = remaining[0];
    if (next) {
      await setActiveSeatingPlan(weddingId, next);
    }
  }
}

export interface MigrationResult {
  migrated: boolean;
  planId?: string;
}

/**
 * One-time migration from root /tables + /roomElements to seatingPlans subcollections.
 */
export async function migrateLegacySeatingIfNeeded(
  wedding: Wedding,
  guests: Guest[],
  defaultPlanName: string,
): Promise<MigrationResult> {
  const weddingId = wedding.id;

  if (wedding.seatingMigrationDone) {
    return { migrated: false, planId: wedding.activeSeatingPlanId ?? undefined };
  }

  const existingPlans = await getDocs(seatingPlansCol(weddingId));
  if (!existingPlans.empty) {
    const firstPlanId = existingPlans.docs[0]!.id;
    const activeId = wedding.activeSeatingPlanId ?? firstPlanId;
    const patch: Record<string, unknown> = {
      seatingMigrationDone: true,
      seatingMigrationInProgress: false,
      updatedAt: serverTimestamp(),
    };
    if (!wedding.activeSeatingPlanId) {
      patch.activeSeatingPlanId = firstPlanId;
      patch.legacySeatingPlanId = wedding.legacySeatingPlanId ?? firstPlanId;
    }
    await updateDoc(weddingRef(weddingId), patch);
    return { migrated: false, planId: activeId };
  }

  let locked = false;
  try {
    locked = await runTransaction(db, async (tx) => {
      const wRef = weddingRef(weddingId);
      const snap = await tx.get(wRef);
      if (!snap.exists()) return false;
      const data = snap.data() as Wedding;
      if (data.seatingMigrationDone || data.seatingMigrationInProgress) return false;
      tx.update(wRef, {
        seatingMigrationInProgress: true,
        updatedAt: serverTimestamp(),
      });
      return true;
    });
  } catch {
    return { migrated: false };
  }

  if (!locked) return { migrated: false };

  try {
    const legacyTables = await getDocs(legacyTablesCol(weddingId));
    const legacyRooms = await getDocs(legacyRoomElementsCol(weddingId));
    const hasLegacy = !legacyTables.empty || !legacyRooms.empty;
    const hasGuestAssignments = guests.some((g) => g.tableId);

    const inset = wedding.seatingCanvasInset
      ? { ...DEFAULT_SEATING_CANVAS_INSET, ...wedding.seatingCanvasInset }
      : DEFAULT_SEATING_CANVAS_INSET;

    const planId = await createSeatingPlan(weddingId, defaultPlanName, {
      seatingCanvasInset: inset,
      seatingRoomWidthM: wedding.seatingRoomWidthM ?? 20,
    });

    if (hasLegacy) {
      const ops: Array<(batch: ReturnType<typeof writeBatch>) => void> = [];
      for (const d of legacyTables.docs) {
        const data = d.data();
        ops.push((batch) =>
          batch.set(planTableRef(weddingId, planId, d.id), {
            ...data,
            seatMap: data.seatMap ?? [],
            guestIds: data.guestIds ?? [],
            hiddenSeats: data.hiddenSeats ?? [],
          }),
        );
      }
      for (const d of legacyRooms.docs) {
        ops.push((batch) => batch.set(planRoomElementRef(weddingId, planId, d.id), d.data()));
      }
      await commitInChunks(ops);

      const guestOps: Array<(batch: ReturnType<typeof writeBatch>) => void> = [];
      for (const g of guests) {
        if (g.tableId) {
          guestOps.push((batch) =>
            batch.update(guestRef(weddingId, g.id), {
              [guestSeatingFieldPath(planId)]: {
                tableId: g.tableId,
                seatNumber: g.seatNumber ?? null,
              },
              updatedAt: serverTimestamp(),
            }),
          );
        }
      }
      if (guestOps.length > 0) await commitInChunks(guestOps);

      const deleteOps: Array<(batch: ReturnType<typeof writeBatch>) => void> = [];
      for (const d of legacyTables.docs) {
        deleteOps.push((batch) => batch.delete(doc(db, "weddings", weddingId, "tables", d.id)));
      }
      for (const d of legacyRooms.docs) {
        deleteOps.push((batch) =>
          batch.delete(doc(db, "weddings", weddingId, "roomElements", d.id)),
        );
      }
      if (deleteOps.length > 0) await commitInChunks(deleteOps);
    } else if (hasGuestAssignments) {
      const guestOps: Array<(batch: ReturnType<typeof writeBatch>) => void> = [];
      for (const g of guests) {
        if (g.tableId) {
          guestOps.push((batch) =>
            batch.update(guestRef(weddingId, g.id), {
              [guestSeatingFieldPath(planId)]: {
                tableId: g.tableId,
                seatNumber: g.seatNumber ?? null,
              },
              updatedAt: serverTimestamp(),
            }),
          );
        }
      }
      if (guestOps.length > 0) await commitInChunks(guestOps);
    } else {
      // Brand-new wedding — default empty plan only.
    }

    await updateDoc(weddingRef(weddingId), {
      activeSeatingPlanId: planId,
      legacySeatingPlanId: hasLegacy || hasGuestAssignments ? planId : null,
      seatingMigrationDone: true,
      seatingMigrationInProgress: false,
      updatedAt: serverTimestamp(),
    });

    return { migrated: true, planId };
  } catch (err) {
    await updateDoc(weddingRef(weddingId), {
      seatingMigrationInProgress: false,
      updatedAt: serverTimestamp(),
    });
    throw err;
  }
}

/**
 * Clears a stale `seatingMigrationInProgress` flag left by an interrupted client
 * migration. Without this, `migrateLegacySeatingIfNeeded` never acquires the lock.
 */
export async function clearStaleSeatingMigrationLock(wedding: Wedding): Promise<void> {
  if (wedding.seatingMigrationDone || !wedding.seatingMigrationInProgress) return;
  await updateDoc(weddingRef(wedding.id), {
    seatingMigrationInProgress: false,
    updatedAt: serverTimestamp(),
  });
}

/** Ensure at least one plan exists (post-migration safety net). */
export async function ensureDefaultSeatingPlan(
  wedding: Wedding,
  defaultPlanName: string,
): Promise<string> {
  const weddingId = wedding.id;
  await clearStaleSeatingMigrationLock(wedding);
  if (wedding.activeSeatingPlanId) {
    const planSnap = await getDoc(seatingPlanRef(weddingId, wedding.activeSeatingPlanId));
    if (planSnap.exists()) return wedding.activeSeatingPlanId;
  }

  const plans = await getDocs(seatingPlansCol(weddingId));
  if (!plans.empty) {
    const id = plans.docs[0]!.id;
    await setActiveSeatingPlan(weddingId, id);
    return id;
  }

  const inset = wedding.seatingCanvasInset
    ? { ...DEFAULT_SEATING_CANVAS_INSET, ...wedding.seatingCanvasInset }
    : DEFAULT_SEATING_CANVAS_INSET;
  const planId = await createSeatingPlan(weddingId, defaultPlanName, {
    seatingCanvasInset: inset,
    seatingRoomWidthM: wedding.seatingRoomWidthM ?? 20,
  });
  await updateDoc(weddingRef(weddingId), {
    activeSeatingPlanId: planId,
    legacySeatingPlanId: planId,
    seatingMigrationDone: true,
    updatedAt: serverTimestamp(),
  });
  return planId;
}

/** Load every seating plan and its tables (for cross-plan guest cleanup). */
export async function fetchAllPlansWithTables(
  weddingId: string,
): Promise<Array<{ planId: string; tables: Table[] }>> {
  const plansSnap = await getDocs(seatingPlansCol(weddingId));
  const result: Array<{ planId: string; tables: Table[] }> = [];

  for (const planDoc of plansSnap.docs) {
    const tablesSnap = await getDocs(planTablesCol(weddingId, planDoc.id));
    const tables = tablesSnap.docs.map((d) => {
      const data = d.data() as Omit<Table, "id">;
      return { id: d.id, ...data, seatMap: data.seatMap ?? [] } as Table;
    });
    result.push({ planId: planDoc.id, tables });
  }

  return result;
}
